import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
} from "@mui/material";
import DirectionsBikeIcon from "@mui/icons-material/DirectionsBike";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import StarIcon from "@mui/icons-material/Star";
import deliveryAgentsApi from "../../api/delivery-agents";
import { format } from "date-fns";

interface DeliveryAgentDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  deliveryAgentId: string | null;
}

const DeliveryAgentDetailsDialog: React.FC<DeliveryAgentDetailsDialogProps> = ({
  open,
  onClose,
  deliveryAgentId,
}) => {
  const [details, setDetails] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && deliveryAgentId) {
      void fetchDetails();
    } else {
      setDetails(null);
    }
  }, [open, deliveryAgentId]);

  const fetchDetails = async () => {
    if (!deliveryAgentId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await deliveryAgentsApi.getDeliveryAgentDetails(deliveryAgentId);
      setDetails(data);
    } catch (err: any) {
      console.error("Error fetching delivery agent details:", err);
      setError(err.response?.data?.message || "Failed to load delivery agent details");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const agent = details?.deliveryAgent;
  const stats = details?.stats;
  const activeOrders = stats?.activeInstantOrders || [];

  const getDutyStatusChip = (status: string, lastSeen?: string, isBusyWithSubscription?: boolean) => {
    const isLivenessActive = lastSeen
      ? new Date().getTime() - new Date(lastSeen).getTime() < 5 * 60 * 1000
      : false;

    const statusLabel =
      status === "ON_DELIVERY"
        ? "ON DELIVERY"
        : isBusyWithSubscription
        ? "ON SUBSCRIPTION"
        : status === "AVAILABLE" && isLivenessActive
        ? "AVAILABLE"
        : status === "AVAILABLE" && !isLivenessActive
        ? "UNREACHABLE"
        : "OFF DUTY";

    switch (statusLabel) {
      case "AVAILABLE":
        return <Chip icon={<CheckCircleIcon />} label="AVAILABLE" color="success" size="small" sx={{ fontWeight: 800 }} />;
      case "ON DELIVERY":
      case "ON SUBSCRIPTION":
        return <Chip icon={<DirectionsBikeIcon />} label={statusLabel} color="warning" size="small" sx={{ fontWeight: 800 }} />;
      case "UNREACHABLE":
        return <Chip label="UNREACHABLE" color="error" size="small" sx={{ fontWeight: 800 }} />;
      case "OFF DUTY":
      default:
        return <Chip label="OFF DUTY" color="default" size="small" sx={{ fontWeight: 800 }} />;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={{ "& .MuiDialog-paper": { borderRadius: 4 } }}>
      <DialogTitle sx={{ fontWeight: 800 }}>Rider Live Status & Details</DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: "#f8fafc" }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={12}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !agent ? (
          <Typography color="text.secondary" align="center">No details available</Typography>
        ) : (
          <Grid container spacing={3}>
            {/* Left Column: Profile Summary */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper sx={{ p: 2.5, borderRadius: 3, height: "100%", boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
                <Box display="flex" flexDirection="column" alignItems="center" textAlign="center" mb={2}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      backgroundColor: "#f97316",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 24,
                      fontWeight: 800,
                      mb: 1.5,
                      boxShadow: "0 4px 10px rgba(249,115,22,0.3)",
                    }}
                  >
                    {agent.name ? agent.name.charAt(0).toUpperCase() : "R"}
                  </Box>
                  <Typography variant="h6" fontWeight={800}>{agent.name}</Typography>
                   <Typography variant="body2" color="text.secondary" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                    {getDutyStatusChip(agent.instantStatus, agent.lastSeen, stats?.isBusyWithSubscription)}
                  </Typography>
                  {agent.rating ? (
                    <Box display="flex" alignItems="center" mt={1} gap={0.5}>
                      <StarIcon sx={{ color: "#eab308", fontSize: "1.15rem" }} />
                      <Typography variant="body2" fontWeight={750}>{agent.rating.toFixed(1)}</Typography>
                      <Typography variant="caption" color="text.secondary">({agent.noOfRatings || 0} reviews)</Typography>
                    </Box>
                  ) : null}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box display="flex" flexDirection="column" gap={1.5}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PhoneIcon sx={{ color: "text.secondary", fontSize: "1.15rem" }} />
                    <Typography variant="body2" fontWeight={600}>{agent.phoneNumber}</Typography>
                  </Box>
                  {agent.email && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <EmailIcon sx={{ color: "text.secondary", fontSize: "1.15rem" }} />
                      <Typography variant="body2" fontWeight={600} sx={{ wordBreak: "break-all" }}>{agent.email}</Typography>
                    </Box>
                  )}
                  {agent.bikeNumber && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <DirectionsBikeIcon sx={{ color: "text.secondary", fontSize: "1.15rem" }} />
                      <Typography variant="body2" fontWeight={600}>Bike: {agent.bikeNumber}</Typography>
                    </Box>
                  )}
                  <Box display="flex" alignItems="center" gap={1}>
                    <CalendarTodayIcon sx={{ color: "text.secondary", fontSize: "1.15rem" }} />
                    <Typography variant="caption" color="text.secondary">
                      Joined: {format(new Date(agent.createdAt), "MMM dd, yyyy")}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* Right Column: Performance and Active Load */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Box display="flex" flexDirection="column" gap={3}>
                {/* Stats row */}
                <Grid container spacing={2}>
                  <Grid size={{ xs: 4 }}>
                    <Paper sx={{ p: 2, borderRadius: 3, textAlign: "center", boxShadow: "0 4px 15px rgba(0,0,0,0.03)" }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>TODAY'S INSTANT</Typography>
                      <Typography variant="h5" fontWeight={800} color="primary.main">{stats?.todayInstantOrdersCount || 0}</Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Paper sx={{ p: 2, borderRadius: 3, textAlign: "center", boxShadow: "0 4px 15px rgba(0,0,0,0.03)" }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>TOTAL INSTANT</Typography>
                      <Typography variant="h5" fontWeight={800} color="primary.main">{stats?.completedInstantOrdersCount || 0}</Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Paper sx={{ p: 2, borderRadius: 3, textAlign: "center", boxShadow: "0 4px 15px rgba(0,0,0,0.03)" }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>SUBSCRIPTION</Typography>
                      <Typography variant="h5" fontWeight={800} color="primary.main">{stats?.totalOrders || 0}</Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Active carrying load */}
                <Paper sx={{ p: 2.5, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
                  <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 2 }}>
                    Current Active Load ({activeOrders.length} orders)
                  </Typography>

                  {activeOrders.length === 0 ? (
                    <Box display="flex" justifyContent="center" py={4}>
                      <Typography color="text.secondary" variant="body2">
                        Rider is not carrying any active orders right now.
                      </Typography>
                    </Box>
                  ) : (
                    <TableContainer component={Box}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ "& th": { fontWeight: 800 } }}>
                            <TableCell>Order #</TableCell>
                            <TableCell>Customer</TableCell>
                            <TableCell>Address</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {activeOrders.map((order: any) => (
                            <TableRow key={order._id}>
                              <TableCell sx={{ fontWeight: 700 }}>{order.orderNumber}</TableCell>
                              <TableCell>{order.userId?.name || "Customer"}</TableCell>
                              <TableCell sx={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {order.deliveryAddress?.addressLine}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={order.status}
                                  size="small"
                                  color={
                                    order.status === "PICKED_UP"
                                      ? "warning"
                                      : order.status === "READY"
                                      ? "info"
                                      : "primary"
                                  }
                                  sx={{ fontWeight: 800, fontSize: "0.675rem" }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Paper>
              </Box>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeliveryAgentDetailsDialog;
